// BHP-1047: normalize Backend2.0 JSON-LD/Hydra responses to plain app models so
// features never touch @context/@id/@type or hydra:member directly.

export interface HydraView {
  '@id'?: string;
  '@type'?: string;
  'hydra:first'?: string;
  'hydra:last'?: string;
  'hydra:next'?: string;
  'hydra:previous'?: string;
}

export interface RawHydraCollection<T> {
  // API Platform 4.x emits unprefixed JSON-LD keys...
  member?: T[];
  totalItems?: number;
  view?: HydraView;
  // ...older versions used the `hydra:`-prefixed form. Accept both.
  'hydra:member'?: T[];
  'hydra:totalItems'?: number;
  'hydra:view'?: HydraView;
}

export interface HydraCollection<T> {
  items: T[];
  total: number;
  view?: HydraView;
}

/** Last path segment of an IRI, e.g. `/api/invoices/abc-123` → `abc-123`. */
export function extractUuidFromIri(
  iri: string | undefined
): string | undefined {
  if (iri === undefined) {
    return undefined;
  }
  const segments = iri.split('/').filter((segment) => segment.length > 0);
  return segments.length > 0 ? segments[segments.length - 1] : undefined;
}

/**
 * Strip JSON-LD keys (`@context`/`@id`/`@type`/…) and guarantee a `uuid`
 * (an explicit `uuid` field wins; otherwise it is derived from `@id`).
 */
export function normalizeItem<T>(raw: Record<string, unknown>): T {
  const clean: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!key.startsWith('@')) {
      clean[key] = value;
    }
  }
  if (clean.uuid === undefined) {
    const uuid = extractUuidFromIri(raw['@id'] as string | undefined);
    if (uuid !== undefined) {
      clean.uuid = uuid;
    }
  }
  return clean as T;
}

export function normalizeCollection<T>(
  raw: RawHydraCollection<Record<string, unknown>>
): HydraCollection<T> {
  const members = raw.member ?? raw['hydra:member'] ?? [];
  return {
    items: members.map((member) => normalizeItem<T>(member)),
    total: raw.totalItems ?? raw['hydra:totalItems'] ?? 0,
    view: raw.view ?? raw['hydra:view']
  };
}
